import {Injectable} from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import {Observable} from 'rxjs';
import {finalize, tap} from 'rxjs/operators';

import {NewRelicBrowserService} from '../services/new-relic-browser.service';

@Injectable()
export class NewRelicObservabilityInterceptor implements HttpInterceptor {
  constructor(private newRelic: NewRelicBrowserService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isFootballMatcherApi(request.url)) {
      return next.handle(request);
    }

    const requestId = request.headers.get('X-Request-ID') || this.createRequestId();
    const context = this.extractContext(request.url);
    let headers = request.headers.set('X-Request-ID', requestId);
    if (context.groupId !== null) {
      headers = headers.set('X-FM-Group-ID', String(context.groupId));
    }
    if (context.gameId !== null) {
      headers = headers.set('X-FM-Game-ID', String(context.gameId));
    }

    const startedAt = Date.now();
    let status = 0;
    return next.handle(request.clone({headers})).pipe(
      tap(
        event => {
          if (event instanceof HttpResponse) {
            status = event.status;
          }
        },
        error => {
          if (error instanceof HttpErrorResponse) {
            status = error.status;
          }
        },
      ),
      finalize(() => {
        const attributes: Record<string, string | number> = {
          'request.id': requestId,
          'http.statusCode': status,
          'duration.ms': Date.now() - startedAt,
        };
        if (context.groupId !== null) {
          attributes['group.id'] = context.groupId;
        }
        if (context.gameId !== null) {
          attributes['game.id'] = context.gameId;
        }
        this.newRelic.addApiRequest(attributes);
      }),
    );
  }

  private isFootballMatcherApi(url: string): boolean {
    if (!/^https:\/\/api(?:-dev|-dev-eb)?\.footballmatcher\.(?:io|com)(?:\/|$)/.test(url)) {
      return false;
    }
    // The Cognito auth passthrough (authBaseUrl, `/auth/...`) isn't proxied through our
    // backend's CORS middleware and rejects the X-Request-ID header in its preflight response.
    return !new URL(url).pathname.startsWith('/auth/');
  }

  private extractContext(url: string): {groupId: number | null; gameId: number | null} {
    const path = new URL(url).pathname;
    const groupMatch = path.match(/\/(?:kickarounds|groups)\/(\d+)(?:\/|$)/);
    const gameMatch = path.match(/\/games\/(\d+)(?:\/|$)/);
    return {
      groupId: this.newRelic.positiveInteger(groupMatch?.[1]),
      gameId: this.newRelic.positiveInteger(gameMatch?.[1]),
    };
  }

  private createRequestId(): string {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
      const randomValue = crypto.getRandomValues(new Uint8Array(1))[0] & 15;
      const value = character === 'x' ? randomValue : (randomValue & 3) | 8;
      return value.toString(16);
    });
  }
}